/* ============================================================================
   proton-cart-polish.js — free-shipping meter for the cart drawer and /cart
   Source of truth: ~/proton/public/storefront/proton-cart-polish.js
   Deployed by:     ~/proton/scripts/_deploy-proton-cart-polish.js
   ----------------------------------------------------------------------------
   🚨 THE THRESHOLD IS NOT TYPED HERE. It comes from window.PROTON_CART.threshold,
      which theme.liquid fills. There are already THREE surfaces claiming
      "Free U.S. shipping on orders $75 and over" (announcement bar, PDP trust
      badge, footer). A fourth one with its own hard-coded number is how they
      drift apart — and a meter that promises free shipping the checkout then
      charges for is a real consumer-protection problem, not a cosmetic bug.

   🚨 The meter reads cart.total_price from /cart.js, which is AFTER discounts —
      the same basis Shopify's own shipping rate uses. Do not switch it to
      items_subtotal_price: a $80 cart with a 10% code ships at $72 and the meter
      would say "free" while checkout charges.
   ========================================================================== */
(function () {
  "use strict";

  var CFG = window.PROTON_CART || {};
  var THRESHOLD = Number(CFG.threshold);      // cents
  var CURRENCY = CFG.currencySymbol || "$";
  var ENABLED = isFinite(THRESHOLD) && THRESHOLD > 0;

  function money(cents) {
    var v = Math.max(0, cents) / 100;
    return CURRENCY + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
  }

  function build() {
    var el = document.createElement("div");
    el.className = "pcart-ship";
    el.setAttribute("role", "status");
    el.innerHTML =
      '<span class="pcart-ship__msg"></span>' +
      '<span class="pcart-ship__track"><span class="pcart-ship__fill"></span></span>';
    return el;
  }

  function render(el, total) {
    var remaining = THRESHOLD - total;
    var pct = Math.max(0, Math.min(100, (total / THRESHOLD) * 100));
    var msg = el.querySelector(".pcart-ship__msg");
    var fill = el.querySelector(".pcart-ship__fill");
    if (remaining <= 0) {
      el.classList.add("is-free");
      msg.textContent = "Congratulations! You get free U.S. shipping.";
      fill.style.width = "100%";
    } else {
      el.classList.remove("is-free");
      msg.innerHTML =
        "You're <span class=\"pcart-ship__amount\"></span> away from free U.S. shipping.";
      msg.querySelector(".pcart-ship__amount").textContent = money(remaining);
      fill.style.width = pct + "%";
    }
  }

  // Mount points for the GUARANTEE line: the drawer footer (under the tax note)
  // and the /cart subtotal block. Both sit directly above the Check out button.
  function mountPoints() {
    var out = [];
    var drawer = document.querySelector(".cart-drawer__footer");
    if (drawer) out.push(drawer);
    var page = document.querySelector(".cart__footer .js-contents, .cart__footer");
    if (page && page !== drawer) out.push(page);
    return out;
  }

  // 🚨 Diego 2026-09-23: the free-shipping meter belongs at the VERY BOTTOM —
  //    BELOW the Check out button, everything else above it. The button is NOT
  //    inside .cart-drawer__footer / .js-contents; it lives in a SIBLING
  //    .cart__ctas (drawer: .drawer__footer > [.cart-drawer__footer, .cart__ctas];
  //    /cart: .cart__blocks > [.js-contents, .cart__ctas, dynamic buttons]).
  //    So the meter mounts on the CTA's PARENT and is appended last — which also
  //    keeps it under PayPal / Shop Pay express buttons when those render.
  function ctaBlocks() {
    var out = [];
    Array.prototype.forEach.call(document.querySelectorAll(".cart__ctas"), function (c) {
      var host = c.parentNode;
      if (host && host.nodeType === 1) out.push({ host: host, ctas: c });
    });
    return out;
  }

  function meterHosts() {
    var out = [];
    ctaBlocks().forEach(function (b) { if (out.indexOf(b.host) < 0) out.push(b.host); });
    return out.length ? out : mountPoints();
  }

  // 🚨 Diego 2026-09-23 wants the guarantee on ONE line. `white-space:nowrap` alone
  //    only moves the failure from a wrap to an ELLIPSIS — measured live, the
  //    sentence overflowed its column by 2px at 12px on the /cart page (1440) and
  //    by 25px at 320px wide. So the type is FITTED: step down until it measures
  //    inside its own box. Floor 9px; the CSS media queries stay as the no-JS
  //    fallback and `text-overflow:ellipsis` as the last-resort guard.
  var GUAR_SIZES = [12, 11.5, 11, 10.5, 10, 9.5, 9];

  function fitGuarantee(g) {
    if (!g || !g.clientWidth) return;
    for (var i = 0; i < GUAR_SIZES.length; i++) {
      g.style.fontSize = GUAR_SIZES[i] + "px";
      if (g.scrollWidth <= g.clientWidth + 1) return;
    }
  }

  function fitAllGuarantees() {
    Array.prototype.forEach.call(document.querySelectorAll(".pcart-guar"), fitGuarantee);
  }

  function place(total) {
    // An EMPTY cart gets no meter — "you're $75 away" on an empty drawer is
    // noise, and Dawn hides the footer anyway.
    meterHosts().forEach(function (host) {
      var el = host.querySelector(":scope > .pcart-ship");
      if (total <= 0) {
        if (el) el.remove();
        return;
      }
      // 🚨 RE-ENTRANCY GUARD. Writing the meter mutates the DOM, the observer
      //    below sees that mutation and calls refresh(), which writes again —
      //    a self-sustaining loop that fired ~30 /cart.js requests per page load
      //    and got this machine a 429 from Shopify. Skip the write when nothing
      //    actually changed; the observer filter below is the second line.
      if (el && el.dataset.total === String(total)) return;
      if (!el) {
        el = build();
        host.appendChild(el);
      } else if (el !== host.lastElementChild) {
        // Dawn re-renders can re-order children; keep the meter LAST.
        host.appendChild(el);
      }
      el.dataset.total = String(total);
      render(el, total);
    });
    // Diego 09-10: the 30-day guarantee reassures at the CHECKOUT decision point
    // (moved here FROM the PDP buy box — "add it here instead of where you just
    // put it"). 🚨 It is inserted as the sibling immediately BEFORE .cart__ctas —
    // NOT appended inside .js-contents. That old mount is a GRID on /cart, and
    // auto-placement dropped the appended line into a later cell: measured live
    // at 428px it rendered at y=883, i.e. BELOW the meter, while its DOM position
    // said otherwise. Sitting in the same flow container as the button and the
    // meter makes DOM order == visual order on both surfaces.
    // No link on purpose: a link leads AWAY from checkout.
    // 🚨 Diego 09-23: this sentence must fit on ONE line — "back." wrapping to a
    //    second line is what he flagged. Shortened (no "if you") and the em dash
    //    is gone; .pcart-guar also sizes down instead of wrapping.
    var blocks = ctaBlocks();
    var kept = [];
    (blocks.length ? blocks : mountPoints().map(function (h) { return { host: h, ctas: null }; }))
      .forEach(function (b) {
        var g = b.host.querySelector(":scope > .pcart-guar");
        if (total <= 0) { if (g) g.remove(); return; }
        if (!g) {
          g = document.createElement("div");
          g.className = "pcart-guar";
          g.innerHTML = "<b>30-Day “Love It” Guarantee</b> · don’t love it, send it back.";
        }
        if (b.ctas) {
          if (g.nextElementSibling !== b.ctas) b.host.insertBefore(g, b.ctas);
        } else if (g.parentNode !== b.host) {
          b.host.appendChild(g);
        }
        kept.push(g);
        fitGuarantee(g);
      });
    // Drop any guarantee left in an old mount — the line must appear ONCE.
    Array.prototype.forEach.call(document.querySelectorAll(".pcart-guar"), function (g) {
      if (kept.indexOf(g) < 0) g.remove();
    });
  }

  var inflight = false;
  var queued = false;

  function refresh() {
    if (!ENABLED) return;
    if (inflight) { queued = true; return; }
    inflight = true;
    fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c) place(Number(c.total_price) || 0); })
      .catch(function () { /* a meter must never break the cart */ })
      .then(function () {
        inflight = false;
        if (queued) { queued = false; refresh(); }
      });
  }

  function boot() {
    if (!ENABLED) return;
    refresh();

    // Dawn re-renders the drawer's innerHTML on every quantity change and on
    // every add-to-cart, which wipes anything we injected. Watch and re-place.
    // 🚨 Debounced: the re-render fires several mutations per update, and an
    //    un-debounced observer would issue a /cart.js request per mutation.
    var t = null;
    var obs = new MutationObserver(function (records) {
      // Ignore mutations we caused ourselves — see the guard in place().
      var external = records.some(function (r) {
        var n = r.target;
        while (n && n.nodeType === 1) {
          if (n.classList && (n.classList.contains("pcart-ship") || n.classList.contains("pcart-guar"))) return false;
          n = n.parentNode;
        }
        return true;
      });
      if (!external) return;
      clearTimeout(t);
      t = setTimeout(refresh, 160);
    });
    var targets = document.querySelectorAll("#CartDrawer, cart-drawer, .cart__footer, cart-items");
    Array.prototype.forEach.call(targets, function (n) {
      obs.observe(n, { childList: true, subtree: true });
    });

    document.addEventListener("shopify:section:load", refresh);

    // The drawer and the /cart column are different widths, and rotating a phone
    // changes both — re-fit the guarantee instead of letting it clip.
    var rt = null;
    window.addEventListener("resize", function () {
      clearTimeout(rt);
      rt = setTimeout(fitAllGuarantees, 150);
    });

    // 🚨 A DOM observer only sees carts changed through Dawn's own UI. This store
    //    also mutates the cart from proton-upsell.js (bundle swap, in-drawer
    //    quick-adds) and from the Coyote pairing logic, which call /cart/add.js
    //    directly — those would move the total with the meter still showing the
    //    old number. Hook fetch so ANY cart write refreshes it.
    //    Pass-through only: the original promise is returned untouched, and the
    //    refresh is fire-and-forget, so a failure here can never break an add.
    if (window.fetch && !window.__pcartFetchHooked) {
      window.__pcartFetchHooked = true;
      var orig = window.fetch;
      window.fetch = function (input, init) {
        var out = orig.apply(this, arguments);
        try {
          var url = typeof input === "string" ? input : (input && input.url) || "";
          if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
            out.then(function () { setTimeout(refresh, 120); }).catch(function () {});
          }
        } catch (e) {}
        return out;
      };
    }
  }

  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);
})();
